import {
  Form,
  Input,
  Select,
  Space,
  type InputRef,
  type FormInstance,
} from 'antd';
import classnames from 'classnames';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatPhoneNumberIntl,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input';

import { CountryFlag } from '@client/components/CountryFlag';
import { CCountryCodes } from '@client/types/phone.type';
import {
  findFieldValue,
  mergeNames,
  requiredField,
} from '@client/utils/form.util';
import { logger } from '@client/utils/logger.util';

import styles from './components.module.less';

import type { ISPhone } from '@common/types';
import type { CountryCode, PhoneNumber } from 'libphonenumber-js';

import 'react-phone-number-input/style.css';

type TPhoneOnChangeValue = string | PhoneNumber | null;

interface IPhoneFieldProps {
  isEdit?: boolean;
  formRef: FormInstance<any>;
  disabled?: boolean;
  className?: string;
  ns?: string[];
  required?: boolean;
  isStrict?: boolean;
  extension?: boolean;
  countryCodes?: CountryCode[];
  availablePhones?: ISPhone[];
  placeholder?: string;
  onBlur?: (value: string) => void;
  onChange?: (value: TPhoneOnChangeValue) => void;
}

export const PhoneField = (props: IPhoneFieldProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<InputRef>(null);

  const {
    isEdit = false,
    formRef,
    disabled = false,
    className,
    ns = ['phone'],
    required = true,
    isStrict = true,
    extension = true,
    countryCodes = CCountryCodes,
    availablePhones = [],
    placeholder = t('form.phone'),
    onBlur,
    onChange,
  } = props;

  const [parsed, setParsed] = useState<PhoneNumber | null>(null);
  const [status, setStatus] = useState<{ msg: string; error: boolean } | null>(
    null,
  );

  const getFieldsValue = useCallback((): {
    _country: CountryCode | undefined;
    _phone: string | undefined;
    _extension: string | undefined;
  } => {
    const fields = formRef?.getFieldsValue();
    const {
      country,
      nationalNumber,
      extension: ext,
    } = (findFieldValue(fields, ns) as any) || {};
    return {
      _country: country as CountryCode | undefined,
      _phone: nationalNumber as string | undefined,
      _extension: ext as string | undefined,
    };
  }, [formRef, ns]);

  const findPhone = useCallback(
    (sPhones: ISPhone[], parsed: PhoneNumber | null): ISPhone | undefined =>
      sPhones.find(
        (p) =>
          p.numberDetails.nationalNumber.replace(/ /g, '') === parsed?.number &&
          p.numberDetails.country === parsed?.country,
      ),
    [],
  );

  const setPhoneNumber = useCallback(
    (phone: string | null = null, ext: string | null = null): void => {
      formRef.setFields([
        { name: mergeNames(ns, 'nationalNumber'), value: phone },
        { name: mergeNames(ns, 'extension'), value: ext },
      ]);
    },
    [formRef, ns],
  );

  const safeParse = useCallback(
    (value?: string, country?: CountryCode): PhoneNumber | null => {
      if (!value) return null;
      try {
        // parsePhoneNumber may throw on invalid input
        return parsePhoneNumber(
          value,
          country as CountryCode,
        ) as unknown as PhoneNumber;
      } catch {
        return null;
      }
    },
    [],
  );

  const getFormatted = useCallback(
    (parsed: PhoneNumber | null): string => {
      let _parsed = parsed;
      if (!parsed) {
        const { _country, _phone } = getFieldsValue();
        _parsed = safeParse(_phone, _country);
      }
      return _parsed ? formatPhoneNumberIntl(_parsed.number) : '';
    },
    [getFieldsValue, safeParse],
  );

  const onPhoneValidator = useCallback((): Promise<void> => {
    const { _country, _phone, _extension } = getFieldsValue();
    const _onReject = (msg = t('form.phone.invalidPhone')): Promise<void> => {
      setStatus({ msg, error: true });
      formRef.setFields([
        {
          name: mergeNames(ns, 'nationalNumber'),
          value: _phone,
          errors: [msg],
        },
      ]);
      return Promise.reject(msg);
    };

    const _parsed = safeParse(_phone, _country);
    if (_parsed) {
      const _isValid = isValidPhoneNumber(_parsed.number);
      const _isPossible = isPossiblePhoneNumber(_parsed.number);
      const _formatted = getFormatted(_parsed);
      const _isIncluded = findPhone(availablePhones, _parsed);
      if (_isIncluded && !isEdit) {
        if (extension) {
          if (
            _isIncluded?.numberDetails?.extension === _extension ||
            (!_isIncluded?.numberDetails?.extension && !_extension)
          ) {
            return _onReject(t('form.phone.exists'));
          }
        } else {
          return _onReject(t('form.phone.exists'));
        }
      }
      if (isStrict ? _isValid : _isPossible) {
        setStatus({ msg: 'success', error: false });
        setPhoneNumber(_formatted, _extension);
        onChange?.(_parsed);
        setParsed(_parsed);
        return Promise.resolve();
      }
      return _onReject();
    }
    return _onReject();
  }, [
    getFieldsValue,
    setStatus,
    formRef,
    ns,
    t,
    findPhone,
    availablePhones,
    isEdit,
    extension,
    isStrict,
    getFormatted,
    onChange,
    setPhoneNumber,
    safeParse,
  ]);

  useEffect(() => {
    const { _phone } = getFieldsValue();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (formRef && _phone) {
      timeoutId = setTimeout(() => {
        onPhoneValidator().catch((error) => logger.warn(error));
      }, 0);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // Only run when formRef or phone changes
  }, [formRef, getFieldsValue, onPhoneValidator]);

  useEffect(() => {
    if (formRef && parsed) {
      formRef?.validateFields().then(
        () => {
          /* empty */
        },
        () => {
          /* empty */
        },
      );
    }
  }, [formRef, parsed]);

  const selectCountry = useCallback((): void => {
    setStatus(null);
    setParsed(null);
    setPhoneNumber();
  }, [setStatus, setPhoneNumber]);

  const countryOptions = useMemo(
    () =>
      countryCodes.map((code: CountryCode) => ({
        value: code,
        label: (
          <div className={styles.flags}>
            <CountryFlag country={code} />
            {code}
          </div>
        ),
      })),
    [countryCodes],
  );

  return (
    <div className={classnames(styles.phoneNumber, className)}>
      <Space.Compact className={styles.phoneSpace}>
        <Form.Item
          noStyle
          name={mergeNames(ns, 'country')}
          rules={[requiredField(t('form.phone.countryCode'), true)]}
        >
          <Select
            showSearch
            disabled={disabled}
            style={{ width: 170 }}
            options={countryOptions}
            onSelect={selectCountry}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate
          hasFeedback
          validateStatus={
            status ? (status?.error ? 'error' : 'success') : undefined
          }
          name={mergeNames(ns, 'nationalNumber')}
          rules={[requiredField(t('form.phone'), required)]}
        >
          <Input
            role={'presentation'}
            disabled={disabled}
            ref={inputRef}
            placeholder={placeholder}
            autoComplete={'off'}
            onChange={(e) => {
              onChange?.(e.target.value);
            }}
            onBlur={(e) => {
              onPhoneValidator().catch((error) => logger.warn(error));
              onBlur?.(e.target.value);
            }}
          />
        </Form.Item>
        {extension ? (
          <Form.Item noStyle name={mergeNames(ns, 'extension')}>
            <Input
              placeholder={t('form.phone.extension')}
              style={{ width: 125 }}
              disabled={disabled}
              autoComplete={'off'}
              onBlur={(e) => onBlur?.(e.target.value)}
            />
          </Form.Item>
        ) : null}
      </Space.Compact>
    </div>
  );
};
